import { Spin } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { MessageContent } from 'features/ask-yogi/message-rendering'
import { TableFilterModal } from 'features/ask-yogi/message-rendering'
import type { Message } from 'features/ask-yogi/types'
import { useFeatureFlagGate } from 'features/project/feature-flags'
import { useTopBar } from 'features/project/project-layout'
import { useProjectStore } from 'features/project/store'
import {
  ContentWrapper,
  InsightDescription,
  InsightHeader,
  InsightTitle,
  PageContainer,
  PageShell,
  SectionCard,
} from 'features/trend-detection/components/insight-detail-styles'
import { InsightDetailSubHeader } from 'features/trend-detection/components/InsightDetailSubHeader'
import { useAgentInsightDetail } from 'features/trend-detection/features/insight-agent/hooks/use-agent-insight-detail'
import { useAgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import { trendDetectionFeedPath } from 'features/trend-detection/paths'

// The flag key is `general_agent`, NOT `insight_agent`: it predates the feature
// rename and carries live per-user overrides a rename would orphan. A non-global
// flag with no override for this project/user is ABSENT from the response, never
// `false`, which is why this coerces rather than compares.
const INSIGHT_AGENT_FLAG = 'general_agent'

export const AgentInsightDetailPage: React.FC = () => {
  const { insightUuid } = useParams<{ insightUuid: string }>()
  const projectId = useProjectStore(
    (s: { projectId: string | null }) => s.projectId,
  )
  const navigate = useNavigate()
  const { setBreadcrumbSubtitle } = useTopBar()
  // Redirects only once the flags request RESOLVES to "off"; a bare
  // `!hasFlag` effect fires while flags are still loading and bounces a user
  // who does have the flag off any hard refresh or deep link.
  useFeatureFlagGate(
    INSIGHT_AGENT_FLAG,
    projectId ? trendDetectionFeedPath(projectId) : '/',
  )
  const service = useAgentInsightService()

  const {
    insight,
    processedParts,
    citationFilters,
    tableFilters,
    isLoading,
    isError,
  } = useAgentInsightDetail(service, projectId, insightUuid)

  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [selectedResultIds, setSelectedResultIds] = useState<string[]>([])

  useEffect(() => {
    setBreadcrumbSubtitle(insight?.title ?? null)
    return () => setBreadcrumbSubtitle(null)
  }, [insight?.title, setBreadcrumbSubtitle])

  // MessageContent renders an assistant turn, so the stored report is adapted
  // into exactly that: the body as `content`, and the compile-time-resolved
  // chart requests as `artifacts.charts` for <Chart> to re-render from.
  const message = useMemo<Message | null>(
    () =>
      insight
        ? {
            role: 'assistant',
            content: insight.content,
            uuid: insight.uuid,
            create_time: insight.create_time,
            artifacts: { charts: insight.artifacts },
          }
        : null,
    [insight],
  )

  const handleBack = (): void => {
    navigate(projectId ? trendDetectionFeedPath(projectId) : '/')
  }

  const handleTableFilterClick = (resultIds: string[]): void => {
    setSelectedResultIds(resultIds)
    setTableModalOpen(true)
  }

  return (
    <PageShell>
      <InsightDetailSubHeader
        onBack={handleBack}
        // Insight-agent insights carry no feedback surface — matching the
        // benchmarking agent, which also hard-disables it.
        feedbackDisabled
      />
      <PageContainer>
        <ContentWrapper>
          {isLoading && <Spin />}
          {isError && !isLoading && (
            <InsightDescription>
              This insight could not be loaded.
            </InsightDescription>
          )}
          {insight && message && processedParts && (
            <>
              <InsightHeader>
                <InsightTitle>{insight.title}</InsightTitle>
                <InsightDescription>{insight.description}</InsightDescription>
              </InsightHeader>
              <SectionCard>
                <MessageContent
                  processedParts={processedParts}
                  message={message}
                  messageIndex={0}
                  projectId={projectId ?? ''}
                  citationFilters={citationFilters}
                  showActions={false}
                  onTableFilterClick={handleTableFilterClick}
                />
              </SectionCard>
            </>
          )}
        </ContentWrapper>
      </PageContainer>
      <TableFilterModal
        open={tableModalOpen}
        onClose={() => setTableModalOpen(false)}
        projectId={projectId ?? ''}
        filterData={selectedResultIds
          .map((id) => tableFilters[id])
          .filter((item): item is NonNullable<typeof item> => Boolean(item))}
      />
    </PageShell>
  )
}
